import { FunctionComponent, SyntheticEvent, useState } from 'react';
import { Game as GameType } from '~types';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Button } from '~ui/index';
import { gql, useMutation } from '@apollo/client';
import { useUnmount, useMount } from 'react-use';
import { useConfig } from '~components/hooks';
import { toast } from 'react-toastify';
import { useYaMetrica } from '~frontend/provider/yametrica';
import { useOpenModal } from '~components/modals/hooks';
import _ from 'lodash';
import { useProfile } from '~components/profile/hooks';
import { SELL_ALL_ITEMS } from '~components/profile/SellAllItems';
import {
  Steps,
  ItemsShop,
  ItemsUser,
  UpgradeLeft,
  MoreItems,
  UpgradeRight,
  UpgradeCenter,
  UpgradeBtns,
} from '~components/upgrade';
import playAudio from '~frontend/utils/playAudio';

import '../../scss/pages/_upgrade.scss';

import {
  useItemRight,
  useUpgradeAmount,
  useIdsItemsLeft,
  useMode,
  useWinItem,
  useItemsLeft,
  useStatusGame,
  useChance,
  useWinGames,
  useLoseGame,
} from '../../components/upgrade/hooks';
import { setTimeout } from 'isomorphic-timers-promises';

interface SignUpgradeData {
  signUpgrade: GameType;
}

interface SignUpgradeVars {
  input: {
    gameIds: number[];
    money: number;
    itemId: number;
  };
}

const Upgrade: FunctionComponent = () => {
  const getConfig = useConfig();
  const [chance, setChance] = useChance();
  const getProfile = useProfile();
  const [itemRight, setItemsRight] = useItemRight();
  const [upgradeAmount, setUpgradeAmount] = useUpgradeAmount();
  const [idsItemsLeft, setIdsItemsLeft] = useIdsItemsLeft();
  const [itemsLeft, setItemsLeft] = useItemsLeft();
  const [mode] = useMode();
  const [, setWinItem] = useWinItem();
  const [, setLooseItem] = useLoseGame();
  const yametrica = useYaMetrica();
  const { onShow } = useOpenModal();
  const [, setStatusGame] = useStatusGame();
  const [winGames, setWinGames] = useWinGames();
  const [tab, setTab] = useState(false);

  const handleTab = () => {
    setTab(tab => !tab);
  };

  const range = itemRight?.price / upgradeAmount;

  const reachGoal = (
    event: SyntheticEvent<HTMLElement>,
    { reachgoal } = event?.currentTarget?.dataset ||
      // eslint-disable-next-line no-unsafe-optional-chaining
      (event.target as HTMLElement)?.dataset,
  ) => {
    yametrica.reachGoal(reachgoal);
  };

  const isAcitve =
    upgradeAmount > 0 &&
    itemRight &&
    chance < 100 &&
    chance > 0 &&
    range <= 15 &&
    itemRight?.price > upgradeAmount;

  const { t } = useTranslation();

  useMount(() => {
    reachGoal(null, { reachgoal: 'goPageUpgrade' });
  });

  useUnmount(() => {
    setStatusGame(0);
    setIdsItemsLeft([]);
    setItemsRight(null);
    setItemsLeft([]);
    setUpgradeAmount(0);
    setWinItem(0);
    setChance(0);
  });

  const [signUpgrade, { loading: loadingMutation, client }] = useMutation<
    SignUpgradeData,
    SignUpgradeVars
  >(
    gql`
      mutation SignUpgrade($input: SignUpgradeInput!) {
        signUpgrade(input: $input) {
          id
          price
          getItem {
            id
            getImage
            getName
          }
        }
      }
    `,
    {
      variables: {
        input: {
          gameIds: idsItemsLeft,
          money: mode === 1 ? upgradeAmount : 0,
          itemId: itemRight?.id,
        },
      },
    },
  );

  const onSignUpgrade = async (event: SyntheticEvent<HTMLElement>) => {
    try {
      event.preventDefault();

      if (!getProfile && _.size(winGames) > 0) {
        return onShow('ForgotModal', { title: 'Wanna real prize?' });
      }

      const {
        data: { signUpgrade: data },
      } = await signUpgrade();

      playAudio(event, '12.mp3', 0.2);

      for (const getItem of itemsLeft) {
        client.cache.modify({
          id: client.cache.identify(getItem as any),
          fields: {
            status() {
              return 5;
            },
            geStatus() {
              return 'In upgrade';
            },
          },
        });
      }

      setStatusGame(2);
      if (data.price < itemRight.price) {
        setLooseItem(data);
      } else {
        setWinItem(data);
      }

      await setTimeout(2000);
      setWinGames(_.concat([], winGames, data));
      setItemsRight(null);
      setItemsLeft([]);
      setUpgradeAmount(0);
      setIdsItemsLeft([]);
      setStatusGame(3);
      playAudio(null, '17.mp3', 0.4);

      await setTimeout(500);

      if (getProfile) {
        await client.refetchQueries({
          include: [
            'ItemsUser',
            {
              query: SELL_ALL_ITEMS,
              variables: {
                userId: getProfile.id,
              },
            },
          ],
        });
      }
    } catch (error) {
      toast.error(
        <>
          <div className="notify-title">{t('Error')}!</div>
          <div className="notify-text">{t(error.message)}</div>
        </>,
      );
    }
  };

  return (
    <>
      <Helmet>
        <title>
          {t('Upgrading CS: GO skins from inventory to cscase', {
            hostname: getConfig?.hostname,
          })}
        </title>
        <meta
          name="description"
          content={t(
            'Use the most profitable and fair cs go skin upgrades! No minimum amount! Transform the chirp into usable cs: go skins! Come in now!',
            {
              hostname: getConfig?.hostname,
            },
          )}
        />
      </Helmet>
      <div className="page-upgrade">
        <h1>{getProfile ? t('Upgrade_H1') : t('Demo_Upgrade_H1')}</h1>
        <div className="upgrade-main-wrap">
          <div className={`upgrade-main ${isAcitve && 'active'}`}>
            <UpgradeLeft />

            <UpgradeCenter />

            <UpgradeRight />
            <div className="upgrade-main-bottom">
              <Button
                className={`btn btn-violet apgrate_button ${
                  isAcitve && !loadingMutation ? 'btn-start' : 'disabled'
                }`}
                loading={loadingMutation}
                onClick={onSignUpgrade}
                data-reachgoal="playupgrade"
                onMouseEnter={playAudio}
                data-audio="2.mp3"
                data-volume="0.15"
              >
                {t('Upgrade')}
              </Button>
            </div>
            <UpgradeBtns />
          </div>
        </div>

        <div className="upgrade-items" data-tab={tab}>
          <div className="upgrade-mob-tabs">
            <div onClick={handleTab} className={!tab ? 'active' : ''}>
              {t('My Items')}
            </div>
            <div onClick={handleTab} className={tab ? 'active' : ''}>
              {t('For an upgrade')}
            </div>
          </div>

          <ItemsUser />
          <ItemsShop />
        </div>

        <Steps />
      </div>
    </>
  );
};

export default Upgrade;
